import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {useLazyGetDataQuery, useLazyGetNextPageQuery} from 'api';

// MUI Components
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Fade from '@mui/material/Fade';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import {Typography} from '@mui/material';

// Styled Components
import {StyledSearchFormWrapper} from 'components/styled';

//Custom components
import SearchResults from 'components/search-results';
import DataSkeleton from 'components/data-skeleton';
import SearchError from './SearchError';
import MoreDataError from './MoreDataError';
import NotFoundError from './NotFoundError';

// Constants
import {SEARCH_PARAMETER_TYPES, PATIENT, PATIENT_FHIR_ID} from 'constants.js';
import {
  SEARCH_FORM_MAIN_INPUT_SEARCH_FIELD_TEST_ID,
  SEARCH_FORM_ORGANIZATION_SEARCH_PARAMETER_SEARCH_FIELD_TEST_ID,
  SEARCH_FORM_SHOW_MORE_BUTTON_TEST_ID,
  SEARCH_FORM_ORGANIZATION_SEARCH_PARAMETER_ADD_BUTTON_TEST_ID
} from 'testidConstants.js';

// REDUX
import {connect} from 'react-redux';
import {
  inputDataSelector,
  inputDataTypeSelector,
  searchPerformedOnSelector,
  optionalOrganizationSearchParameterSelector,
  optionalOrganizationSearchParameterTypeSelector,
  isOrganizationSearchParameterShownSelector,
  isOrganizationSearchParameterBarOpenedSelector,
  setInputData,
  setInputDataType,
  setSearchPerformedOn,
  setOptionalOrganizationSearchParameter,
  setOptionalOrganizationSearchParameterType,
  setIsOrganizationSearchParameterShown,
  setIsOrganizationSearchParameterBarOpened
} from 'redux/slices/searchFormSlice';
import {
  setResultData,
  setNextPageData,
  resultDataSelector,
  setIsResultDataFetching,
  setNotFoundError,
  setIsSearchResultDataError,
  isResultDataFetchingSelector,
  notFoundErrorSelector,
  isSearchResultDataErrorSelector
} from 'redux/slices/searchResultsSlice';
import {setAuthError, rightsSelector} from 'redux/slices/appSlice';
import {setResourcesData} from 'redux/slices/searchResultsSlice';
import {
  setIsSimilarResourceSearch,
  setResourceId,
  isSimilarResourceSearchSelector,
  resourceIdSelector,
  searchedTypeSelector,
  setSearchedType
} from 'redux/slices/similarResourceSearchSlice';

const selectInputItems = SEARCH_PARAMETER_TYPES.flatMap((group, index) => {
  return [
    {type: 'header', key: index, value: group.types[0]?.id, label: group.groupLabel},
    group.types.map((searchType) => {
      return {type: 'menuItem', key: searchType.id, value: searchType, label: searchType.label};
    })
  ];
}).flat();

const organizationSelectInputItems = SEARCH_PARAMETER_TYPES[1].types.map((searchType) => {
  return {key: searchType.id, value: searchType, label: searchType.label};
});

const SearchForm = ({
  setAppAuthError,
  setSearchFormInputData,
  setSearchFormInputDataType,
  setSearchFormSearchPerformedOn,
  setSearchFormOptionalOrganizationSearchParameter,
  setSearchFormOptionalOrganizationSearchParameterType,
  setSearchFormIsOrganizationSearchParameterShown,
  setSearchFormIsOrganizationSearchParameterBarOpened,
  setSearchResultsResultData,
  setSearchResultsNextPageData,
  setSearchResultsResourcesData,
  setSearchResultsNotFoundError,
  setSearchResultsIsSearchResultDataError,
  setSearchResultsIsResultDataFetching,
  setSimilarResourceSearchIsSimilarResourceSearch,
  setSimilarResourceSearchResourceId,
  setSimilarResourceSearchSearchedType,
  rights,
  inputData,
  inputDataType,
  searchPerformedOn,
  optionalOrganizationSearchParameter,
  optionalOrganizationSearchParameterType,
  isOrganizationSearchParameterShown,
  isOrganizationSearchParameterBarOpened,
  resultData,
  notFoundError,
  isSearchResultDataError,
  isResultDataFetching,
  isSimilarResourceSearch,
  similarResourceSearchResourceId,
  similarResourceSearchedType
}) => {
  const [isMoreResultDataError, setIsMoreResultDataError] = useState(false);

  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));

  const [getData, {data: searchResultData, isFetching: isDataFetching, error: searchResultDataError}] =
    useLazyGetDataQuery();

  const [
    getMoreData,
    {
      data: moreResultData,
      isFetching: isMoreResultDataFetching,
      error: searchMoreResultDataError,
      isSuccess: isMoreResultDatatSuccess
    }
  ] = useLazyGetNextPageQuery();

  const handleInputDataTypeChange = (event) => {
    setSearchFormInputDataType(event.target.value);
    handleOrganizationSearchParameterVisibilityChange(event.target.value);
  };

  const handleOptionalOrganizationSearchParameterTypeChange = (event) => {
    setSearchFormOptionalOrganizationSearchParameterType(event.target.value);
  };

  const handleSubmitForm = (event) => {
    event.preventDefault();
    setSearchResultsResultData({});
    setSearchResultsResourcesData({});
    setSearchResultsNotFoundError(false);
    setSearchResultsIsSearchResultDataError(false);
    setIsMoreResultDataError(false);

    setSimilarResourceSearchIsSimilarResourceSearch(false);
    setSimilarResourceSearchResourceId('');
    setSimilarResourceSearchSearchedType({});

    getData({
      inputData,
      inputDataType,
      optionalOrganizationSearchParameter,
      optionalOrganizationSearchParameterType
    });
  };

  const handleInputDataChange = (event) => {
    setSearchFormInputData(event.target.value);
  };

  const handleInputDataTrimmingSpaces = (event) => {
    setSearchFormInputData(event.target.value.trim());
  };

  const handleInputOrganizationSearchParameterChange = (event) => {
    setSearchFormOptionalOrganizationSearchParameter(event.target.value);
  };

  const handleInputOrganizationSearchParameterTrimmingSpaces = (event) => {
    setSearchFormOptionalOrganizationSearchParameter(event.target.value.trim());
  };

  const handleOrganizationSearchParameterVisibilityChange = (value) => {
    if (value?.group == PATIENT) {
      setSearchFormIsOrganizationSearchParameterShown(true);
      resetOrganizationSearchParameterBar();
    } else {
      setSearchFormIsOrganizationSearchParameterShown(false);
      resetOrganizationSearchParameterBar();
    }
  };

  const resetOrganizationSearchParameterBar = () => {
    setSearchFormIsOrganizationSearchParameterBarOpened(false);
    setSearchFormOptionalOrganizationSearchParameter('');
  };

  useEffect(() => {
    if (searchResultData && !searchResultDataError && !isDataFetching) {
      setSearchFormSearchPerformedOn(inputDataType.group);
      if (searchResultData && searchResultData.count > 0) {
        setSearchResultsResultData(searchResultData);
        setSearchResultsNotFoundError(false);
      } else {
        setSearchResultsResultData({});
        setSearchResultsNotFoundError(true);
      }
    }
  }, [searchResultData, searchResultDataError, isDataFetching]);

  useEffect(() => {
    setSearchResultsIsResultDataFetching(isDataFetching);
  }, [isDataFetching]);

  useEffect(() => {
    if (searchResultDataError?.status === 401 || searchMoreResultDataError?.status === 401) {
      setAppAuthError({is: true, errorScope: !rights.search});
    } else if (searchResultDataError || searchMoreResultDataError) {
      searchMoreResultDataError && setIsMoreResultDataError(true);
      if (searchResultDataError) {
        setSearchResultsIsSearchResultDataError(true);
        setSearchResultsResultData({});
      }
    }
  }, [searchResultDataError, searchMoreResultDataError]);

  useEffect(() => {
    if (moreResultData) {
      setSearchResultsNextPageData(moreResultData);
    }
    if (isMoreResultDatatSuccess) {
      setIsMoreResultDataError(false);
    }
  }, [moreResultData]);

  const changeOrganizationSearchParameterBarStatus = (status) => {
    setSearchFormIsOrganizationSearchParameterBarOpened(status.enabled);
    setSearchFormIsOrganizationSearchParameterShown(!status.enabled);
    setSearchFormOptionalOrganizationSearchParameter('');
  };

  return (
    <StyledSearchFormWrapper>
      <form onSubmit={handleSubmitForm}>
        <Grid container alignItems="center" justifyContent="center" direction="row" style={{padding: '24px 24px 0px'}}>
          <Grid item container xs={12} md={3}>
            <FormControl variant="outlined" style={{alignSelf: 'baseline', width: '100%'}}>
              <InputLabel id="select-searchby-label">Search By</InputLabel>
              <Select
                aria-label="Search By"
                label="Search By"
                labelId="select-searchby-label"
                name="searchParameter"
                value={inputDataType}
                onChange={handleInputDataTypeChange}
              >
                {selectInputItems.map((item) => {
                  switch (item.type) {
                    case 'header':
                      return (
                        <ListSubheader key={item.key} value={item.value}>
                          {item.label}
                        </ListSubheader>
                      );
                    case 'menuItem':
                      return (
                        <MenuItem key={item.key} value={item.value} label={item.label} style={{width: '100%'}}>
                          {item.label}
                        </MenuItem>
                      );
                  }
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item container xs={12} md={7} lg={8} style={{padding: isMobile ? '16px 0' : '0 0 0 16px'}}>
            <FormControl style={{width: '100%'}}>
              <TextField
                required
                value={inputData}
                onChange={handleInputDataChange}
                onBlur={handleInputDataTrimmingSpaces}
                inputProps={SEARCH_FORM_MAIN_INPUT_SEARCH_FIELD_TEST_ID}
                label={`Enter ${inputDataType.label}`}
              ></TextField>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={2} lg={1} style={{padding: '0 0 0 16px'}}>
            {!isMobile && (
              <Button type="submit" color="primary" variant="contained" disabled={isResultDataFetching}>
                Search
              </Button>
            )}
          </Grid>

          {isOrganizationSearchParameterShown && (
            <>
              <Grid item xs={12}>
                <Button
                  style={{textTransform: 'none', marginBottom: isMobile && '16px'}}
                  disabled={inputDataType === PATIENT_FHIR_ID}
                  aria-label="id-organization-search-parameter-button-add"
                  size="small"
                  color="primary"
                  data-testid={SEARCH_FORM_ORGANIZATION_SEARCH_PARAMETER_ADD_BUTTON_TEST_ID}
                  onClick={() => changeOrganizationSearchParameterBarStatus({enabled: true})}
                >
                  <AddIcon style={{marginTop: 5}} />
                  <Typography variant="body2" style={{marginTop: 5, marginLeft: 5}}>
                    Search by managing organization
                  </Typography>
                </Button>
              </Grid>
            </>
          )}

          {isOrganizationSearchParameterBarOpened && (
            <>
              <Grid item container xs={12} md={3} style={{marginTop: 10}}>
                <FormControl variant="outlined" style={{alignSelf: 'baseline', width: '100%'}}>
                  <InputLabel id="select-searchby-label">Search By</InputLabel>
                  <Select
                    aria-label="Search By"
                    label="Search By"
                    labelId="select-searchby-organization-label"
                    name="searchOrganizationParameter"
                    value={optionalOrganizationSearchParameterType}
                    onChange={handleOptionalOrganizationSearchParameterTypeChange}
                  >
                    {organizationSelectInputItems.map((item) => {
                      return (
                        <MenuItem key={item.key} value={item.value} label={item.label} style={{width: '100%'}}>
                          {item.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                container
                xs={12}
                md={7}
                lg={8}
                style={{marginTop: 10, padding: isMobile ? '16px 0' : '0 0 0 16px'}}
              >
                <FormControl style={{width: '100%'}}>
                  <TextField
                    required
                    value={optionalOrganizationSearchParameter}
                    onChange={handleInputOrganizationSearchParameterChange}
                    onBlur={handleInputOrganizationSearchParameterTrimmingSpaces}
                    inputProps={SEARCH_FORM_ORGANIZATION_SEARCH_PARAMETER_SEARCH_FIELD_TEST_ID}
                    label={`Enter ${optionalOrganizationSearchParameterType.label}`}
                  ></TextField>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2} lg={1} style={{padding: isMobile ? '16px 0' : '0'}}>
                <Button
                  sx={{
                    textTransform: 'none',
                    transition: '0.5s',
                    '&:hover': {
                      color: 'rgb(40, 92, 118)',
                      backgroundColor: 'transparent'
                    }
                  }}
                  disableRipple={true}
                  aria-label="clear"
                  size="small"
                  color="primary"
                  onClick={() => changeOrganizationSearchParameterBarStatus({enabled: false})}
                >
                  <ClearIcon style={{marginTop: 15}} />
                  {isMobile && (
                    <Typography variant="body2" style={{marginTop: 5, marginLeft: 5}}>
                      Clear search by managing organization
                    </Typography>
                  )}
                </Button>
              </Grid>
            </>
          )}
          {isMobile && (
            <Button
              style={{width: '100%'}}
              type="submit"
              color="primary"
              variant="contained"
              disabled={isResultDataFetching}
            >
              Search
            </Button>
          )}
        </Grid>
      </form>

      {isSearchResultDataError && <SearchError />}

      {notFoundError && <NotFoundError />}

      {resultData.resources && (
        <Grid container style={{padding: '32px 32px 0px'}}>
          <Typography style={{alignSelf: 'baseline'}} variant="body2">
            Found {resultData.count} {searchPerformedOn}
            {resultData.count > 1 && 's'}
            {isSimilarResourceSearch &&
              ` with same ${similarResourceSearchedType.label} as ${searchPerformedOn} with FHIR ID '${similarResourceSearchResourceId}'`}
          </Typography>
        </Grid>
      )}

      <Grid container style={{padding: 24}}>
        {isResultDataFetching ? (
          <StyledSearchFormWrapper>
            <DataSkeleton></DataSkeleton>
          </StyledSearchFormWrapper>
        ) : (
          resultData.resources?.map((data, index) => (
            <StyledSearchFormWrapper key={index}>
              <Fade in={true}>
                <div style={{width: '100%'}}>
                  <SearchResults resultData={data} searchPerformedOn={searchPerformedOn}></SearchResults>
                </div>
              </Fade>
            </StyledSearchFormWrapper>
          ))
        )}
      </Grid>

      {isMoreResultDataError && !searchResultDataError && <MoreDataError />}

      {resultData?.nextPageLink && !isResultDataFetching && (
        <Grid container justifyContent="center" alignItems="center" direction="column">
          <Button
            style={{marginBottom: 16}}
            disabled={isMoreResultDataFetching}
            onClick={() => getMoreData(resultData.nextPageLink)}
            color="primary"
            variant="contained"
            data-testid={SEARCH_FORM_SHOW_MORE_BUTTON_TEST_ID}
          >
            {isMoreResultDataError ? 'Retry' : 'Show more'}
            {resultData.isLoading && <CircularProgress size={24} style={{marginLeft: 16}}></CircularProgress>}
          </Button>
        </Grid>
      )}
    </StyledSearchFormWrapper>
  );
};

SearchForm.propTypes = {
  setAppAuthError: PropTypes.func,
  setSearchFormInputData: PropTypes.func,
  setSearchFormInputDataType: PropTypes.func,
  setSearchFormSearchPerformedOn: PropTypes.func,
  setSearchFormOptionalOrganizationSearchParameter: PropTypes.func,
  setSearchFormOptionalOrganizationSearchParameterType: PropTypes.func,
  setSearchFormIsOrganizationSearchParameterShown: PropTypes.func,
  setSearchFormIsOrganizationSearchParameterBarOpened: PropTypes.func,
  setSearchResultsResultData: PropTypes.func,
  setSearchResultsNextPageData: PropTypes.func,
  setSearchResultsResourcesData: PropTypes.func,
  setSearchResultsNotFoundError: PropTypes.func,
  setSearchResultsIsSearchResultDataError: PropTypes.func,
  setSearchResultsIsResultDataFetching: PropTypes.func,
  setSimilarResourceSearchIsSimilarResourceSearch: PropTypes.func,
  setSimilarResourceSearchResourceId: PropTypes.func,
  setSimilarResourceSearchSearchedType: PropTypes.func,
  rights: PropTypes.object,
  inputDataType: PropTypes.object,
  optionalOrganizationSearchParameterType: PropTypes.object,
  resultData: PropTypes.object,
  inputData: PropTypes.string,
  searchPerformedOn: PropTypes.string,
  optionalOrganizationSearchParameter: PropTypes.string,
  isOrganizationSearchParameterShown: PropTypes.bool,
  isOrganizationSearchParameterBarOpened: PropTypes.bool,
  notFoundError: PropTypes.bool,
  isSearchResultDataError: PropTypes.bool,
  isResultDataFetching: PropTypes.bool,
  isSimilarResourceSearch: PropTypes.bool,
  similarResourceSearchResourceId: PropTypes.string,
  similarResourceSearchedType: PropTypes.object
};

const mapStateToProps = (state) => ({
  rights: rightsSelector(state),
  inputData: inputDataSelector(state),
  inputDataType: inputDataTypeSelector(state),
  searchPerformedOn: searchPerformedOnSelector(state),
  optionalOrganizationSearchParameter: optionalOrganizationSearchParameterSelector(state),
  optionalOrganizationSearchParameterType: optionalOrganizationSearchParameterTypeSelector(state),
  isOrganizationSearchParameterShown: isOrganizationSearchParameterShownSelector(state),
  isOrganizationSearchParameterBarOpened: isOrganizationSearchParameterBarOpenedSelector(state),
  resultData: resultDataSelector(state),
  isResultDataFetching: isResultDataFetchingSelector(state),
  notFoundError: notFoundErrorSelector(state),
  isSearchResultDataError: isSearchResultDataErrorSelector(state),
  isSimilarResourceSearch: isSimilarResourceSearchSelector(state),
  similarResourceSearchResourceId: resourceIdSelector(state),
  similarResourceSearchedType: searchedTypeSelector(state)
});

const mapDispatchToProps = (dispatch) => {
  return {
    setAppAuthError: (error) => dispatch(setAuthError(error)),
    setSearchFormInputData: (inputData) => dispatch(setInputData(inputData)),
    setSearchFormInputDataType: (dataType) => dispatch(setInputDataType(dataType)),
    setSearchFormSearchPerformedOn: (searchPerformedOn) => dispatch(setSearchPerformedOn(searchPerformedOn)),
    setSearchFormOptionalOrganizationSearchParameter: (searchParameter) =>
      dispatch(setOptionalOrganizationSearchParameter(searchParameter)),
    setSearchFormOptionalOrganizationSearchParameterType: (searchParameterType) =>
      dispatch(setOptionalOrganizationSearchParameterType(searchParameterType)),
    setSearchFormIsOrganizationSearchParameterShown: (searchParameterShown) =>
      dispatch(setIsOrganizationSearchParameterShown(searchParameterShown)),
    setSearchFormIsOrganizationSearchParameterBarOpened: (searchParameterOpen) =>
      dispatch(setIsOrganizationSearchParameterBarOpened(searchParameterOpen)),
    setSearchResultsResultData: (data) => dispatch(setResultData(data)),
    setSearchResultsNextPageData: (data) => dispatch(setNextPageData(data)),
    setSearchResultsResourcesData: (data) => dispatch(setResourcesData(data)),
    setSearchResultsIsResultDataFetching: (data) => dispatch(setIsResultDataFetching(data)),
    setSearchResultsNotFoundError: (data) => dispatch(setNotFoundError(data)),
    setSearchResultsIsSearchResultDataError: (data) => dispatch(setIsSearchResultDataError(data)),
    setSimilarResourceSearchIsSimilarResourceSearch: (data) => dispatch(setIsSimilarResourceSearch(data)),
    setSimilarResourceSearchResourceId: (data) => dispatch(setResourceId(data)),
    setSimilarResourceSearchSearchedType: (data) => dispatch(setSearchedType(data))
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(SearchForm);
